import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { ToastContent } from "$global-providers/toast-provider/toast-content";
import { styled } from "@material-ui/core";
import React, { useState } from "react";
import { useInterval } from "usehooks-ts";

const useAreChangesStillUnsynced = (
  gracePeriodInMs = 5000,
  pollingIntervalInMs = 1000,
) => {
  const {
    replicationState: { hasUnsyncedChanges },
  } = useRxDBContext();
  const [firstUnsyncedAt, setFirstUnsyncedAt] = useState<Date | null>(null);
  const [areChangesStillUnsynced, setAreChangesStillUnsynced] = useState(false);

  useInterval(() => {
    if (!hasUnsyncedChanges) {
      // Data is synced - reset everything
      setFirstUnsyncedAt(null);
      setAreChangesStillUnsynced(false);
      return;
    }

    // Data is unsynced
    const now = new Date();

    if (!firstUnsyncedAt) {
      // First time seeing unsynced data - record the timestamp
      setFirstUnsyncedAt(now);
      setAreChangesStillUnsynced(false); // Don't show toast yet
      return;
    }

    // Check if grace period has elapsed
    const timeSinceFirstUnsynced = now.getTime() - firstUnsyncedAt.getTime();
    setAreChangesStillUnsynced(timeSinceFirstUnsynced >= gracePeriodInMs);
  }, pollingIntervalInMs);

  return areChangesStillUnsynced;
};

export const UnsyncedToast: React.FC<{
  gracePeriodInMs?: number;
  pollingIntervalInMs?: number;
}> = ({ gracePeriodInMs = 5000, pollingIntervalInMs = 1000 }) => {
  const areChangesStillUnsynced = useAreChangesStillUnsynced(
    gracePeriodInMs,
    pollingIntervalInMs,
  );

  return (
    areChangesStillUnsynced && (
      <BottomFloatingContainer>
        <ToastContent
          message="Syncing with the server"
          variant="loading"
          style={{ height: 36, width: 172, minWidth: 230 }}
        />
      </BottomFloatingContainer>
    )
  );
};

const BottomFloatingContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "-42px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1100,
}));
